import { API_URL } from "@/lib/utils";
import { apiDownloadFile, apiFetchJson } from "@/services/apiClient";

export interface AffiniaUsersSsoConfigBulkImportResult {
  inserted_count: number;
  inserted_emails: string[];
  skipped_count: number;
  skipped_emails: string[];
  duplicate_count: number;
  duplicate_emails: string[];
}

export class BulkImportError extends Error {
  readonly problems: string[];
  constructor(message: string, problems: string[] = []) {
    super(message);
    this.name = "BulkImportError";
    this.problems = problems;
  }
}

async function readBulkImportError(res: Response): Promise<BulkImportError> {
  try {
    const body = await res.json();
    const detail = body?.detail;
    if (typeof detail === "string") return new BulkImportError(detail);
    if (detail?.message) {
      const problems = Array.isArray(detail.problems)
        ? (detail.problems as unknown[]).filter(
            (p): p is string => typeof p === "string",
          )
        : [];
      return new BulkImportError(detail.message as string, problems);
    }
  } catch {
    /* ignore */
  }
  return new BulkImportError("Bulk import failed.");
}

async function uploadImportFile<T>(url: string, file: File): Promise<T> {
  const body = new FormData();
  body.append("file", file);

  const token = localStorage.getItem("access_token");
  const res = await fetch(url, {
    method: "POST",
    body,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (res.status === 401) {
    window.dispatchEvent(new Event("auth:logout"));
    throw new BulkImportError("Your session expired. Please sign in again.");
  }
  if (!res.ok) throw await readBulkImportError(res);
  return res.json();
}


export function bulkImportAffiniaUsersSsoConfig(
  file: File,
): Promise<AffiniaUsersSsoConfigBulkImportResult> {
  return uploadImportFile<AffiniaUsersSsoConfigBulkImportResult>(
    `${API_URL}/affinia-users-sso-config/bulk-import`,
    file,
  );
}

export function downloadAffiniaUsersSsoConfigTemplate(): Promise<void> {
  return apiDownloadFile(
    `${API_URL}/affinia-users-sso-config/template`,
    "Affinia-Users-SSO-Configurations-Template.xlsx",
  );
}


export type ImportLogStatus = "success" | "failure";

export interface ImportLogEntry {
  id: string;
  uploaded_by: string;
  uploaded_at: string;   
  filename: string;
  has_file: boolean; 
  status: ImportLogStatus;
  error_message: string | null;
  total_rows: number;
  inserted_count: number;
  skipped_count: number;
  duplicate_count: number;
}

export interface ImportLogPage {
  items: ImportLogEntry[];
  total: number;
  skip: number;
  limit: number;
}

export interface FetchImportLogsParams {
  skip?: number;
  limit?: number;
  status?: ImportLogStatus;
  uploaded_by?: string;
}
export function fetchImportLogs(
  params: FetchImportLogsParams = {},
): Promise<ImportLogPage> {
  const search = new URLSearchParams();
  if (params.skip !== undefined) search.set("skip", String(params.skip));
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.status) search.set("status", params.status);
  if (params.uploaded_by) search.set("uploaded_by", params.uploaded_by);
  const qs = search.toString();
  return apiFetchJson<ImportLogPage>(
    `${API_URL}/affinia-users-sso-config/import-log${qs ? `?${qs}` : ""}`,
  );
}

export function downloadImportLogFile(
  logId: string, filename: string,
): Promise<void> {
  return apiDownloadFile(
    `${API_URL}/affinia-users-sso-config/import-log/${encodeURIComponent(logId)}/file`,
    filename,
  );
}
